import { PUBLIC_ROUTES } from './../constants/public-routes';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_LOCALSTORAGE_KEYS, AuthService } from '@umss/shared/data-access';
import { secondsUntilExpiry, showDevWarning } from '@umss/shared/utils';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AppContext } from '../services';
import { AppBootstrapStore } from '../services/app-bootstrap.service';

export function refreshAccount(): Promise<void> {
  const bootstrap = inject(AppBootstrapStore);
  const router = inject(Router);
  const appContext = inject(AppContext, { optional: true });

  // Define public routes that don't require authentication
  const publicRoutes = PUBLIC_ROUTES;
  // Use window.location.pathname instead of router.url because router might not be initialized yet
  const currentUrl = window.location.pathname;
  const isPublicRoute = publicRoutes.some((route) =>
    currentUrl.startsWith(route)
  );

  const auth = inject(AuthService);

  // IMPORTANT: Check public route FIRST, before any authentication logic
  // This ensures public routes work regardless of authentication state
  if (isPublicRoute) {
    bootstrap.ready.set(true);
    return Promise.resolve();
  }

  // Store the initial URL to return to after refresh
  const initialUrl = currentUrl + window.location.search + window.location.hash;

  const hasRecentSession = auth.hasRecentSessionEvidence();

  // If there is no recent session evidence, skip refresh and force login.
  if (!hasRecentSession) {
    showDevWarning(
      'No recent session evidence found. Redirecting to login page.',
      {
        currentState: auth.authState(),
        initialUrl,
        localStorage: {
          expiredAt: localStorage.getItem(AUTH_LOCALSTORAGE_KEYS.expAt),
          user: localStorage.getItem(AUTH_LOCALSTORAGE_KEYS.user),
          permissions: localStorage.getItem(AUTH_LOCALSTORAGE_KEYS.permissions),
          facilityIds: localStorage.getItem(AUTH_LOCALSTORAGE_KEYS.facilityIds),
          lastSessionAt: localStorage.getItem(
            AUTH_LOCALSTORAGE_KEYS.lastSessionAt
          )
        }
      }
    );
    return router.navigate(['/auth/login']).then(() => {
      bootstrap.ready.set(true);
    });
  }

  const tryRefreshToken = () => {
    const token = auth.accessToken;
    const remainingSeconds = secondsUntilExpiry(token);

    // Skip bootstrap refresh only when there is a valid token with enough time left.
    if (token && remainingSeconds !== null && remainingSeconds > 60) {
      return of(void 0);
    }

    // Always call refreshToken on app load for private routes.
    // Only treat user as authenticated if refresh succeeds.
    return auth.refreshToken();
  };

  return firstValueFrom(tryRefreshToken())
    .then(() =>
      // Always refresh user data on app load to keep it in sync.
      firstValueFrom(auth.refreshUser().pipe(catchError(() => of(void 0))))
    )
    .then(() => appContext?.ensureLoaded() ?? Promise.resolve())
    .then(() => {
      bootstrap.ready.set(true);
      // Restore initial deep link if needed.
      if (
        router.url === '/' ||
        router.url === '/auth/login' ||
        router.url === ''
      ) {
        router.navigateByUrl(initialUrl);
      }
    })
    .catch(async (error) => {
      // If refresh fails, go to login FIRST, then set ready
      showDevWarning(
        'Failed to refresh token during app initialization. Redirecting to login page.',
        error
      );
      if (!isPublicRoute) {
        await router.navigate(['/auth/login']);
      }
      bootstrap.ready.set(true);
    });
}
